import filters, { Types as TypesFilters } from '@/app/shared/store/modules/Filters'
import breadCrumb, { Types as TypesBreadCrumb } from '@/app/shared/store/modules/BreadCrumb'
import Filter from '@/app/shared/models/Filter'
import OneDimensionStrategy from '@/app/shared/models/OneDimensionStrategy'
import PayloadParametersBuilder from '@/app/shared/models/PayloadParametersBuilder'

import { getLongGrid, downloadGridToXlsx } from '@/app/shared/Services'
import { addNamespace } from '@/app/shared/utils/NamespaceHelper'

const buildFilter = (params, currentDimension) => {
  const { key, type } = currentDimension
  const value = [params.data[key]]
  return new Filter({ key, type, value })
}

const buildLongGridParameters = ({ metrics, factTableName, dimension, strategyFilter, filters, calculatedField, dateFilters }) => {
  const oneDimensionStrategy = new OneDimensionStrategy(dimension, strategyFilter)
  const payloadParametersBuilder = new PayloadParametersBuilder({ metrics, factTableName, filters })
  const parameters = payloadParametersBuilder.build(oneDimensionStrategy)
  parameters.calculated_field = calculatedField
  parameters.date_filters = dateFilters
  return { parameters }
}

const downloadLongGridToXlsx = async (parameters) => {
  const longGridParamaters = buildLongGridParameters(parameters)
  await downloadGridToXlsx(longGridParamaters)
}

const NAMESPACE = 'longGrid/'
const BREADCRUMB_MODULE_NAME = 'breadCrumb'
const NAMESPACE_BREADCRUMB = BREADCRUMB_MODULE_NAME + '/'

const _types = {
  getters: {
    PIVOTED_FILTERS: 'GETTER_PIVOTED_FILTERS',
    BASE_DIMENSION: 'BASE_DIMENSION',
    DIMENSION_BY_ID: 'DIMENSION_BY_ID',
    PIVOT_DIMENSIONS: 'PIVOT_DIMENSIONS',
    PIVOTED_FILTER_DIMENSION_IDS: 'PIVOTED_FILTER_DIMENSION_IDS'
  },
  actions: {
    INIT: 'INIT',
    INIT_BREADCRUMB: 'INIT_BREADCRUMB',
    INIT_LONG_GRID: 'INIT_LONG_GRID',
    INIT_FILTERS: 'INIT_FILTERS',
    CHANGE_BASE_DIMENSION: 'CHANGE_BASE_DIMENSION',
    FETCH_LONG_GRID: 'FETCH_LONG_GRID',
    PUSH_BREADCRUMB_ITEM: 'PUSH_BREADCRUMB_ITEM',
    POP_UNTIL_BREADCRUMB: 'POP_UNTIL_BREADCRUMB',
    CHANGE_PIVOT: 'CHANGE_PIVOT',
    APPLY_FILTERS: 'APPLY_FILTERS',
    DOWNLOAD_GRID_TO_EXCEL: 'DOWNLOAD_GRID_TO_EXCEL',
    RESET: 'RESET'
  },
  mutations: {
    SET_DEFAULT_PARAMETERS: 'SET_DEFAULT_PARAMETERS',
    SET_DIMENSIONS: 'SET_DIMENSIONS',
    SET_LONG_GRID: 'SET_LONG_GRID'
  }
}
export const Types = addNamespace(NAMESPACE, _types)

const LongGrid = {
  namespaced: true,
  state: () => ({
    grid: null,
    dimensions: null
  }),
  getters: {
    [_types.getters.DIMENSION_BY_ID]: (state) => (dimensionId) => {
      return state.dimensions.find((dimension) => dimension.id === dimensionId)
    },
    [_types.getters.PIVOT_DIMENSIONS]: (state, getters, rootState, rootGetters) => {
      const dimensionIdsInBreadCrumb = getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.DIMENSION_IDS_IN_BREADCRUMB]
      const dimensionsToPivot = (dimension) => !dimensionIdsInBreadCrumb.includes(dimension.id)
      return state.dimensions.filter(dimensionsToPivot)
    },
    [_types.getters.PIVOTED_FILTERS]: (state) => {
      return state.breadCrumb.items.filter((breadcrumbItem) => breadcrumbItem.filter)
        .map((breadcrumbItem) => breadcrumbItem.filter)
    },
    [_types.getters.PIVOTED_FILTER_DIMENSION_IDS]: (state, getters) => {
      return getters[_types.getters.PIVOTED_FILTERS].map((pivotedFilter) => pivotedFilter.keyField)
    },
    [_types.getters.DIMENSION_TO_FILTER]: (state, getters) => {
      return getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.DIMENSION_TO_FILTER]
    },
    [_types.getters.BASE_DIMENSION]: (state, getters) => {
      return getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.FIRST_ITEM].dimension
    }
  },
  actions: {
    async [_types.actions.INIT] ({ commit, dispatch }, { dimensions, defaultDimension, defaultParameters, filters }) {
      commit(_types.mutations.SET_DIMENSIONS, dimensions)
      commit(_types.mutations.SET_DEFAULT_PARAMETERS, defaultParameters)
      dispatch(_types.actions.INIT_BREADCRUMB, defaultDimension)
      dispatch(_types.actions.INIT_FILTERS, filters)
      await dispatch(_types.actions.INIT_LONG_GRID, defaultDimension)
    },
    [_types.actions.INIT_FILTERS] ({ dispatch }, filters) {
      const storeFilters = { ...filters.selectedStoreFilters }
      const productFilters = { ...filters.selectedProductFilters }
      dispatch(TypesFilters.actions.SET_SELECTED_FILTERS, { storeFilters, productFilters })
    },
    [_types.actions.INIT_BREADCRUMB] ({ dispatch, commit, state, getters }, dimension) {
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.RESET, null)
      const breadCrumbItemText = dimension.value
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.PUSH_ITEM, { dimension, filter: undefined, text: breadCrumbItemText })
    },
    async [_types.actions.INIT_LONG_GRID] ({ state, dispatch }, dimension) {
      await dispatch(_types.actions.FETCH_LONG_GRID, { dimension })
    },
    async [_types.actions.FETCH_LONG_GRID] ({ state, commit }, { dimension, filter }) {
      commit(_types.mutations.SET_LONG_GRID, null)
      const { metrics, filters, calculatedField, factTableName, dateFilters } = state
      const longGridPayload = buildLongGridParameters({ metrics, factTableName, dimension, strategyFilter: filter, filters, calculatedField, dateFilters })
      const longGrid = (await getLongGrid(longGridPayload))
      commit(_types.mutations.SET_LONG_GRID, longGrid)
    },
    async [_types.actions.APPLY_FILTERS] ({ state, dispatch, getters }, { storeFilters, productFilters }) {
      dispatch(TypesFilters.actions.SET_SELECTED_FILTERS, { storeFilters, productFilters })
      const breadCrumbItem = getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM]
      await dispatch(_types.actions.FETCH_LONG_GRID, breadCrumbItem)
    },
    async [_types.actions.CHANGE_BASE_DIMENSION] ({ dispatch, getters }, dimensionId) {
      const dimension = getters[_types.getters.DIMENSION_BY_ID](dimensionId)
      const pivotedFilters = getters[_types.getters.PIVOTED_FILTERS]
      pivotedFilters.forEach((pivotedFilter) => {
        dispatch(TypesFilters.actions.REMOVE_SELECTED_FILTER_IN_CASCADE, pivotedFilter)
      })
      dispatch(_types.actions.INIT_BREADCRUMB, dimension)
      await dispatch(_types.actions.INIT_LONG_GRID, dimension)
    },
    async [_types.actions.CHANGE_PIVOT] ({ getters, commit, dispatch }, { params, option }) {
      const dimension = getters[_types.getters.DIMENSION_BY_ID](option.id)
      const dimensionToFilter = getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.DIMENSION_TO_FILTER]
      const pivotedDimensionCellValue = params.data[dimensionToFilter.id]
      const breadCrumbItemText = `${dimension.value} en ${pivotedDimensionCellValue}`
      const filter = buildFilter(params, dimensionToFilter)
      await dispatch(_types.actions.PUSH_BREADCRUMB_ITEM, { dimension, filter, breadCrumbItemText })
    },
    async [_types.actions.PUSH_BREADCRUMB_ITEM] ({ commit, dispatch }, { dimension, filter, breadCrumbItemText }) {
      await dispatch(_types.actions.FETCH_LONG_GRID, { dimension, filter })
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.PUSH_ITEM, { dimension, filter, text: breadCrumbItemText })
      commit(TypesFilters.mutations.ADD_SELECTED_FILTER, filter)
    },
    async [_types.actions.POP_UNTIL_BREADCRUMB] ({ state, commit, dispatch, getters }, breadCrumbItem) {
      if (getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM] === breadCrumbItem) return
      while (getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM] !== breadCrumbItem) {
        const lastBreadCrumbItem = getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM]
        commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.REMOVE_ITEM, lastBreadCrumbItem)
        dispatch(TypesFilters.actions.REMOVE_SELECTED_FILTER_IN_CASCADE, lastBreadCrumbItem.filter)
      }
      await dispatch(_types.actions.FETCH_LONG_GRID, breadCrumbItem)
    },
    async [_types.actions.DOWNLOAD_GRID_TO_EXCEL] ({ state, getters }) {
      const { metrics, filters, calculatedField, factTableName, dateFilters } = state
      const { dimension, filter: strategyFilter } = getters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM]
      await downloadLongGridToXlsx({ metrics, dimension, factTableName, strategyFilter, filters, calculatedField, dateFilters })
    },
    async [_types.actions.RESET] ({ commit }) {
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.RESET, null)
      commit(_types.mutations.SET_LONG_GRID, null)
    }
  },
  mutations: {
    [_types.mutations.SET_DEFAULT_PARAMETERS] (state, { metrics, dateFilters, factTableName, calculatedField }) {
      state.metrics = metrics
      state.calculatedField = calculatedField
      state.dateFilters = dateFilters
      state.factTableName = factTableName
    },
    [_types.mutations.SET_LONG_GRID] (state, longGrid) {
      state.grid = longGrid
    },
    [_types.mutations.SET_DIMENSIONS] (state, dimensions) {
      state.dimensions = dimensions
    }
  },
  modules: {
    filters,
    breadCrumb
  }
}

export default LongGrid



// WEBPACK FOOTER //
// ./src/app/shared/store/modules/LongGrid.js